import SearchOutlined from "@mui/icons-material/SearchOutlined";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { useGet } from "../../hooks/api";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { gridClasses } from "@mui/material/Grid";
import { range } from "../../../actprep-ui/utils/helpers";
import { standardDateFormat } from "../../utils/calendar";
import Stack from "@mui/material/Stack";
import debounce from "lodash.debounce";
import Tune from "@mui/icons-material/Tune";
import Edit from "@mui/icons-material/Edit";
import Add from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
  GridToolbarDensitySelector,
  GridActionsCellItem,
} from "@mui/x-data-grid-pro";
import { useActionMenu } from "../../hooks/ui";
import useDisclosure from "../../hooks/ui/useDisclosure";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { sortingModes } from "../../constants/dataGrid";
import { useConfirm } from "material-ui-confirm";

function ToolBar({
  tableName,
  actions,
  FiltersAppliedList,
  onFiltersClick,
  actionsDisabled,
  handleAddRowClick,
  hasCreationForm,
  addRowButtonText,
  resetGridState,
}) {
  const { handleOpen: handleOpenActionMenu, ActionMenu } = useActionMenu({
    actions: actions || [],
  });

  return (
    <>
      <Stack>
        <GridToolbarContainer>
          <GridToolbarColumnsButton />
          <GridToolbarDensitySelector
            slotProps={{ tooltip: { title: "Change density" } }}
          />
          {onFiltersClick && (
            <Button startIcon={<Tune />} onClick={onFiltersClick}>
              Filters
            </Button>
          )}
          <GridToolbarExport
            csvOptions={{
              fileName: `${tableName}_${standardDateFormat(new Date())}`,
            }}
            slotProps={{
              tooltip: { title: "Export data" },
            }}
          />
          <Box
            sx={{
              flexGrow: 1,
            }}
          />

          {actions && (
            <Button onClick={handleOpenActionMenu} disabled={actionsDisabled}>
              Actions
            </Button>
          )}
          {hasCreationForm && (
            <Button startIcon={<Add />} onClick={handleAddRowClick}>
              {addRowButtonText}
            </Button>
          )}
          <Tooltip title="Reset Table Settings">
            <Button
              onClick={() => {
                resetGridState();
              }}
            >
              Reset
            </Button>
          </Tooltip>
        </GridToolbarContainer>
        {FiltersAppliedList && <FiltersAppliedList />}
      </Stack>
      <ActionMenu />
    </>
  );
}

export default function PaginatedTable({
  sx,
  enabled,
  tableName,
  apiUrl,
  urlParams,
  DataGrid: ProvidedDataGridComponent,
  columns: providedColumns,
  onRowClick,
  disableCellFocus,
  searchable,
  onFiltersClick,
  FiltersAppliedList,
  actions,
  reload,
  setReload,
  getRowTitle,
  EditForm,
  CreationForm,
  EditFormDialogProps = {},
  CreationFormDialogProps = {},
  pageSizeOptions,
  sortingMode = sortingModes.CLIENT,
  defaultOrderBy,
  addRowButtonText = "Add Row",
  columnWidthFactor = 1,
  alwaysShowActions = false,
  pagination = true,
  ...props
}) {
  const DataGridComponent = ProvidedDataGridComponent ?? DataGridPro;
  const [columns, setColumns] = useState(providedColumns);
  const [searchInput, setSearchInput] = useState("");
  const [tempSearchInput, setTempSearchInput] = useState("");
  const [actionsDisabled, setActionsDisabled] = useState(!alwaysShowActions);
  const actionsHandledRef = useRef(false);
  const editable = useMemo(() => !!EditForm, [EditForm]);
  const gridApiRef = useGridApiRef();
  const {
    onOpen: onOpenEditForm,
    onClose: onCloseEditForm,
    getDisclosureProps: getEditFormDialogProps,
  } = useDisclosure();
  const [editingRowInfo, setEditingRowInfo] = useState({
    id: null,
    row: null,
  });
  const [openCreationForm, setOpenCreationForm] = useState(false);
  const [newlyCreatedRow, setNewlyCreatedRow] = useState(null);
  const columnWidthFactorAppliedRef = useRef(false);
  const confirm = useConfirm();

  const formatMuiSortModel = (sortModel) => {
    if (!sortModel) {
      return;
    }
    return sortModel.map(
      (sortingRule) =>
        `${sortingRule.sort == "desc" ? "-" : ""}${sortingRule.field.replaceAll(
          ".",
          "__"
        )}`
    );
  };

  const formatDjangoSortModel = (sortModel) => {
    if (!sortModel) {
      return;
    }

    return sortModel.map((sortingRule) => ({
      sort: sortingRule[0] == "-" ? "desc" : "asc",
      field: sortingRule.replace("-", "").replaceAll("__", "."),
    }));
  };
  const defaultGridState = {
    sortModel: formatDjangoSortModel(defaultOrderBy) ?? [],
    density: "standard",
    columnVisibilityModel: {},
    pinnedColumns: props.initialState?.pinnedColumns ?? {
      left: [],
      right: ["actions"],
    },
    filterModel: { items: [] },
  };
  const existingModelState = tableName
    ? JSON.parse(localStorage.getItem(`${tableName}__TableState`)) ??
      defaultGridState
    : defaultGridState;
  const [gridState, setGridState] = useState(existingModelState);

  const selectedRowIds = useRef([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearchInput = useCallback(
    debounce((value) => {
      setSearchInput(value);
    }, 300),
    []
  );

  const handleSearch = (event) => {
    const { value } = event.target;
    setTempSearchInput(value);
    debouncedSetSearchInput(value);
  };

  const handleGridStateChange = (columnModelName, state) => {
    if (tableName) {
      setGridState((curr) => {
        const newState = {
          ...curr,
          [columnModelName]: state,
        };
        localStorage.setItem(
          `${tableName}__TableState`,
          JSON.stringify(newState)
        );
        return newState;
      });
    }
  };

  sx = sx || {};
  if (disableCellFocus) {
    sx = {
      ...sx,
      [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: "none",
      },
      [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
        {
          outline: "none",
        },
    };
  }

  const { data, isLoading, refetch } = useGet(apiUrl, {
    enabled: enabled !== false,
    params: {
      ...urlParams,
      search_input: searchInput,
      page: gridState.paginationModel?.page || 0,
      size: gridState.paginationModel?.pageSize || 50,
      order_by:
        sortingMode == sortingModes.SERVER
          ? formatMuiSortModel(gridState.sortModel) ?? []
          : [],
    },
    staleTime: editable ? 0 : undefined,
  });

  const rowCountRef = useRef(data?.total_count || 0);
  const rowCount = useMemo(() => {
    if (data?.total_count !== undefined) {
      rowCountRef.current = data.total_count;
    }
    return rowCountRef.current;
  }, [data?.total_count]);

  const dataWithIds = (data?.data || []).map((item, i) => ({ id: i, ...item }));
  const handleRowSelectionChange = (selectedIds) => {
    selectedRowIds.current = [...selectedIds];
    if (!alwaysShowActions) {
      setActionsDisabled(selectedRowIds.current.length === 0);
    }
  };

  if (actions?.length) {
    actions = actions.map((action) => ({
      ...action,
      onClick: (event) => {
        action.onClick(
          event,
          selectedRowIds.current.map((id) => dataWithIds[id])
        );
      },
    }));
  }

  const editGridAction = useCallback(
    (row, id) => (
      <Tooltip title="Edit" key={1}>
        <GridActionsCellItem
          icon={<Edit color="primary" />}
          label="Edit"
          onClick={() => {
            setEditingRowInfo(() => ({ id: id, row: row }));
            onOpenEditForm();
          }}
        />
      </Tooltip>
    ),
    [onOpenEditForm]
  );

  useEffect(() => {
    if (reload && setReload) {
      refetch();
      setReload(false);
    }
  }, [reload, setReload, refetch]);

  useEffect(() => {
    if (!editable || actionsHandledRef.current) {
      return;
    }
    const actionColIdx = columns.findIndex((val) => val.type == "actions");
    if (actionColIdx == -1) {
      setColumns((columns) => [
        ...columns,
        {
          field: "actions",
          type: "actions",
          width: 25,
          getActions: ({ row, id }) => {
            return [editGridAction(row, id)];
          },
        },
      ]);
    } else {
      const newCols = [...columns];
      const initialGetActions = newCols[actionColIdx].getActions;
      newCols[actionColIdx].getActions = ({ row, id }) => {
        return [...initialGetActions({ row, id }), editGridAction(row, id)];
      };
      setColumns(newCols);
    }
    actionsHandledRef.current = true;
  }, [editable, columns, editGridAction]);

  const handleEditFormSubmit = useCallback(
    (rowData) => {
      refetch();
      onCloseEditForm();
      if (rowData) {
        setEditingRowInfo((currEditingRowInfo) => ({
          id: currEditingRowInfo.id,
          row: {
            ...currEditingRowInfo.row,
            ...rowData,
          },
        }));
      }
    },
    [refetch, onCloseEditForm, setEditingRowInfo]
  );

  const handleCloseEditForm = useCallback(() => {
    onCloseEditForm();
  }, [onCloseEditForm]);

  const handleCreateFormSubmit = useCallback(
    (createdRow) => {
      refetch();
      setNewlyCreatedRow(createdRow);
    },
    [refetch]
  );

  useEffect(() => {
    if (newlyCreatedRow && dataWithIds?.length > 0) {
      const row = dataWithIds.find((row) => row.uuid === newlyCreatedRow.uuid);

      if (row) {
        setEditingRowInfo({ id: row.id, row });
        onOpenEditForm();
        setNewlyCreatedRow(null);
        setOpenCreationForm(false);
      }
    }
  }, [newlyCreatedRow, dataWithIds, onOpenEditForm]);

  const handleAddRowClick = () => {
    setOpenCreationForm(true);
  };
  if (!columnWidthFactorAppliedRef.current) {
    for (let column of columns) {
      if (column.width && !column.disableResponsiveWidth) {
        column.width = parseInt(column.width * columnWidthFactor);
      }
    }
    columnWidthFactorAppliedRef.current = true;
  }

  return (
    <>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          height: "100%",
        }}
      >
        {searchable && (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              mb: 1,
              flex: "1",
              flexGrow: "0",
            }}
          >
            <SearchOutlined sx={{ color: "action.active", mr: 1 }} />
            <TextField
              label="Search"
              variant="standard"
              fullWidth
              value={tempSearchInput}
              onChange={handleSearch}
              size="small"
            />
          </Box>
        )}
        <Box
          sx={{
            flex: "9",
            flexGrow: "1",
            height: "90%",
          }}
        >
          <DataGridComponent
            sx={{
              ...sx,
            }}
            columnHeaderHeight={40}
            apiRef={gridApiRef}
            pagination={pagination}
            paginationMode="server"
            rowCount={rowCount}
            rows={dataWithIds}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={pageSizeOptions || [...range(10, 100, 10), 200]}
            checkboxSelection={actions?.length > 0}
            onRowSelectionModelChange={(model) => {
              let ids;
              if (model.type == "exclude") {
                ids = dataWithIds
                  .filter(({ id }) => !model.ids.has(id))
                  .map(({ id }) => id);
              } else {
                ids = model.ids;
              }
              handleRowSelectionChange(ids);
            }}
            onColumnVisibilityModelChange={(model) => {
              handleGridStateChange("columnVisibilityModel", model, "columns");
            }}
            onDensityChange={(density) => {
              handleGridStateChange("density", density);
            }}
            onFilterModelChange={(model) => {
              handleGridStateChange("filterModel", model, "filter");
            }}
            onPinnedColumnsChange={(pinnedColumns) =>
              handleGridStateChange("pinnedColumns", pinnedColumns, "columns")
            }
            disableRowSelectionOnClick
            onPaginationModelChange={(newPaginationModel) => {
              handleGridStateChange(
                "paginationModel",
                newPaginationModel,
                "pagination"
              );
            }}
            onSortModelChange={(sortModel) => {
              handleGridStateChange("sortModel", sortModel);
            }}
            onRowClick={onRowClick}
            slots={{
              toolbar: ToolBar,
            }}
            showToolbar
            slotProps={{
              toolbar: {
                tableName,
                actions,
                FiltersAppliedList,
                onFiltersClick,
                actionsDisabled,
                handleAddRowClick,
                hasCreationForm: !!CreationForm,
                addRowButtonText,
                resetGridState: () => {
                  confirm({
                    title: "Reset Table Settings",
                    description:
                      "Are you sure you want to reset your table settings?",
                  })
                    .then(() => {
                      localStorage.removeItem(`${tableName}_TableState`);
                      setGridState(defaultGridState);
                    })
                    .catch(() => {});
                },
              },
            }}
            sortingMode={sortingMode}
            {...props}
            {...gridState}
          />
        </Box>
      </Stack>

      {CreationForm && (
        <Dialog
          open={openCreationForm}
          onClose={() => setOpenCreationForm(false)}
          maxWidth="lg"
          fullWidth
          {...CreationFormDialogProps}
        >
          <DialogTitle>Create New Row</DialogTitle>
          <DialogContent>
            <CreationForm onCreate={handleCreateFormSubmit} />
          </DialogContent>
        </Dialog>
      )}

      {editable && (
        <Dialog
          {...getEditFormDialogProps({
            maxWidth: "lg",
          })}
          onClose={handleCloseEditForm}
          fullWidth
          {...EditFormDialogProps}
        >
          <DialogTitle>
            Editing:{" "}
            <strong>
              {getRowTitle
                ? getRowTitle(editingRowInfo)
                : `Row ${editingRowInfo.id + 1}`}
            </strong>
          </DialogTitle>
          <DialogContent>
            <EditForm
              {...editingRowInfo}
              onEdit={handleEditFormSubmit}
              onClose={onCloseEditForm}
              tableName={tableName}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
